import React from "react";

/**
 * Skeleton primitives.
 * ===========================================================================
 * One object at three shapes, so the liquidity, portfolio and leaderboard
 * pages all wait the same way. The box itself is authored once in `index.css`
 * as `.ce-skel` — a recessed well in the neutral ramp with a light sweep — and
 * these are the React handles for it.
 *
 * The rule for using them: a skeleton mirrors the *shape of the thing that is
 * coming*. Same row height, same column count, same card. That is the whole
 * difference between a skeleton and a spinner — the page does not reflow when
 * the data lands, because the space was already the right size.
 *
 * `index` staggers the sweep across a stack, so eight rows read as one wave
 * rather than eight synchronised strobes.
 */

/** A single slot. `w`/`h` take any CSS length (number = px). */
export const Skeleton = ({
  w = "100%",
  h = 16,
  radius,
  index,
  className = "",
  style,
}) => (
  <div
    className={`ce-skel ${className}`}
    style={{
      width: w,
      height: h,
      ...(radius !== undefined ? { borderRadius: radius } : null),
      ...(index !== undefined ? { "--i": index } : null),
      ...style,
    }}
  />
);

/** A pending line of text — a pill, because that is what a line of type is. */
export const SkeletonLine = (props) => (
  <Skeleton {...props} className={`ce-skel-line ${props.className || ""}`} />
);

/** A pending avatar, rank badge or asset icon. */
export const SkeletonCircle = ({ size = 32, ...rest }) => (
  <Skeleton
    {...rest}
    w={size}
    h={size}
    className={`ce-skel-circle ${rest.className || ""}`}
  />
);

/**
 * A pending eyebrow-over-figure pair — the shape every stat tile on these
 * three pages resolves to, so it is worth having once.
 */
export const SkeletonStat = ({ index, labelWidth = 64, valueWidth = 96 }) => (
  <>
    <SkeletonLine w={labelWidth} h={10} index={index} />
    <SkeletonLine w={valueWidth} h={22} index={index} style={{ marginTop: 8 }} />
  </>
);

export default Skeleton;
